import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  Typography,
  Card,
  Row,
  Col,
  Alert,
  Segmented,
  Switch,
  Input,
  Tag,
  message,
} from 'antd';
import {
  SyncOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { TrialBalanceResponse, TrialBalanceItem } from '../../services/accountingService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 2 }).format(val || 0);

const MONTHS = [
  { value: 0, label: 'Enero' },
  { value: 1, label: 'Febrero' },
  { value: 2, label: 'Marzo' },
  { value: 3, label: 'Abril' },
  { value: 4, label: 'Mayo' },
  { value: 5, label: 'Junio' },
  { value: 6, label: 'Julio' },
  { value: 7, label: 'Agosto' },
  { value: 8, label: 'Septiembre' },
  { value: 9, label: 'Octubre' },
  { value: 10, label: 'Noviembre' },
  { value: 11, label: 'Diciembre' },
];

const TrialBalanceView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  // Period mode: 'month' or 'range'
  const [filterMode, setFilterMode] = useState<'month' | 'range'>('month');
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [hideZeroes, setHideZeroes] = useState(true);

  const [reportData, setReportData] = useState<TrialBalanceResponse | null>(null);

  const getDateRange = () => {
    if (filterMode === 'month') {
      const start = dayjs().year(selectedYear).month(selectedMonth).startOf('month').format('YYYY-MM-DD');
      const end = dayjs().year(selectedYear).month(selectedMonth).endOf('month').format('YYYY-MM-DD');
      return { start, end };
    } else {
      return {
        start: customRange[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
        end: customRange[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
      };
    }
  };

  const loadReport = async () => {
    if (!company.current.id) return;
    try {
      const { start, end } = getDateRange();
      const res = await securePost('/accounting/reports/trial-balance', {
        company_id: company.current.id,
        start_date: start,
        end_date: end,
        level: selectedLevel || undefined,
      });
      const report = res?.data?.report || res?.data || res?.report;
      if (report) {
        setReportData(report);
      }
    } catch (err) {
      console.error('Error loading trial balance:', err);
      message.error('No se pudo generar el Balance de Comprobación.');
    }
  };

  useEffect(() => {
    if (company.current.id) {
      loadReport();
    }
  }, [company.current.id, filterMode, selectedYear, selectedMonth, customRange, selectedLevel]);

  const filteredAccounts = useMemo(() => {
    if (!reportData?.accounts) return [];
    return reportData.accounts.filter((acc) => {
      const matchesSearch =
        acc.code.toLowerCase().includes(searchText.toLowerCase()) ||
        acc.name.toLowerCase().includes(searchText.toLowerCase());

      const hasActivity =
        acc.initial_debit !== 0 ||
        acc.initial_credit !== 0 ||
        acc.period_debit !== 0 ||
        acc.period_credit !== 0 ||
        acc.final_debit !== 0 ||
        acc.final_credit !== 0;

      return matchesSearch && (!hideZeroes || hasActivity);
    });
  }, [reportData, searchText, hideZeroes]);

  const exportToCSV = () => {
    if (!reportData?.accounts?.length) return;
    const headers = [
      'Código',
      'Cuenta',
      'Nivel',
      'Naturaleza',
      'Inicial Débito',
      'Inicial Crédito',
      'Movimiento Débito',
      'Movimiento Crédito',
      'Final Débito',
      'Final Crédito',
    ];

    const rows = filteredAccounts.map((a) => [
      `"${a.code}"`,
      `"${a.name.replace(/"/g, '""')}"`,
      a.level,
      a.nature,
      a.initial_debit,
      a.initial_credit,
      a.period_debit,
      a.period_credit,
      a.final_debit,
      a.final_credit,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `balance_comprobacion_${reportData.start_date}_al_${reportData.end_date}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const totals = reportData?.totals;
  const isBalanced = totals?.is_balanced ?? true;

  const columns = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 110,
      render: (code: string, record: TrialBalanceItem) => {
        const isHeader = record.level <= 2;
        return (
          <Text strong={isHeader} style={{ color: isHeader ? '#1677ff' : '#262626', whiteSpace: 'nowrap' }}>
            {code}
          </Text>
        );
      },
    },
    {
      title: 'Nombre de la Cuenta',
      dataIndex: 'name',
      key: 'name',
      width: 280,
      render: (name: string, record: TrialBalanceItem) => {
        const indent = Math.max(0, (record.level - 1) * 14);
        return (
          <div style={{ paddingLeft: indent }}>
            <Text strong={record.level <= 2}>{name}</Text>
          </div>
        );
      },
    },
    {
      title: 'Saldos Iniciales',
      children: [
        {
          title: 'Débito',
          dataIndex: 'initial_debit',
          key: 'initial_debit',
          align: 'right' as const,
          width: 120,
          render: (val: number) => (val ? <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(val)}</span> : '—'),
        },
        {
          title: 'Crédito',
          dataIndex: 'initial_credit',
          key: 'initial_credit',
          align: 'right' as const,
          width: 120,
          render: (val: number) => (val ? <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(val)}</span> : '—'),
        },
      ],
    },
    {
      title: 'Movimientos del Período',
      children: [
        {
          title: 'Débito',
          dataIndex: 'period_debit',
          key: 'period_debit',
          align: 'right' as const,
          width: 130,
          render: (val: number) => (val ? <Text strong style={{ color: '#1677ff', whiteSpace: 'nowrap' }}>{formatCurrency(val)}</Text> : '—'),
        },
        {
          title: 'Crédito',
          dataIndex: 'period_credit',
          key: 'period_credit',
          align: 'right' as const,
          width: 130,
          render: (val: number) => (val ? <Text strong style={{ color: '#722ed1', whiteSpace: 'nowrap' }}>{formatCurrency(val)}</Text> : '—'),
        },
      ],
    },
    {
      title: 'Saldos Finales',
      children: [
        {
          title: 'Débito',
          dataIndex: 'final_debit',
          key: 'final_debit',
          align: 'right' as const,
          width: 130,
          render: (val: number) => (val ? <Text strong style={{ whiteSpace: 'nowrap' }}>{formatCurrency(val)}</Text> : '—'),
        },
        {
          title: 'Crédito',
          dataIndex: 'final_credit',
          key: 'final_credit',
          align: 'right' as const,
          width: 130,
          render: (val: number) => (val ? <Text strong style={{ whiteSpace: 'nowrap' }}>{formatCurrency(val)}</Text> : '—'),
        },
      ],
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Balance de Comprobación y Saldos</Title>
          <Text type="secondary">
            {reportData
              ? `Período comprendido desde el ${dayjs(reportData.start_date).format('DD/MM/YYYY')} hasta el ${dayjs(reportData.end_date).format('DD/MM/YYYY')}`
              : 'Verificación contable de sumas iguales y saldos'}
          </Text>
        </div>
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={exportToCSV} disabled={!filteredAccounts.length}>
            Exportar CSV
          </Button>
          <Button icon={<SyncOutlined spin={loading} />} onClick={loadReport}>
            Refrescar
          </Button>
        </Space>
      </div>

      {/* Filter Controls Card */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text strong style={{ fontSize: 13 }}>Modalidad de Período:</Text>
              <Segmented
                value={filterMode}
                onChange={(val) => setFilterMode(val as 'month' | 'range')}
                options={[
                  { label: 'Por Mes / Año', value: 'month' },
                  { label: 'Rango Personalizado', value: 'range' },
                ]}
              />
            </Space>
          </Col>

          <Col xs={24} md={10}>
            {filterMode === 'month' ? (
              <Space wrap>
                <Select
                  value={selectedYear}
                  onChange={setSelectedYear}
                  style={{ width: 110 }}
                >
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>
                <Select
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  style={{ width: 140 }}
                >
                  {MONTHS.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
              </Space>
            ) : (
              <RangePicker
                value={customRange}
                onChange={(dates) => dates && setCustomRange([dates[0]!, dates[1]!])}
                format="DD/MM/YYYY"
              />
            )}
          </Col>

          <Col xs={24} md={6}>
            <Select
              placeholder="Profundidad de Cuentas"
              value={selectedLevel}
              onChange={setSelectedLevel}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value={1}>Nivel 1 - Clase (Mayor)</Option>
              <Option value={2}>Nivel 2 - Grupo</Option>
              <Option value={3}>Nivel 3 - Cuenta</Option>
              <Option value={4}>Nivel 4 - Subcuenta</Option>
              <Option value={5}>Nivel 5 - Auxiliares Detallados</Option>
            </Select>
          </Col>
        </Row>

        <Row gutter={[16, 16]} align="middle" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Buscar por código o cuenta..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Switch checked={hideZeroes} onChange={setHideZeroes} />
              <Text>Ocultar cuentas sin movimientos ni saldo</Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Balance Verification Banner */}
      {totals && (
        <Alert
          type={isBalanced ? 'success' : 'error'}
          showIcon
          icon={isBalanced ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span>
                <strong>{isBalanced ? 'BALANCE CUADRADO' : 'DESCUADRE CONTABLE'}</strong>
                {' — '}
                {isBalanced
                  ? `Sumas iguales confirmadas en Débito (${formatCurrency(totals.period_debit)}) y Crédito (${formatCurrency(totals.period_credit)}).`
                  : `Existe una diferencia de ${formatCurrency(Math.abs(totals.difference || 0))} entre débitos y créditos del período.`}
              </span>
              <Tag color={isBalanced ? 'green' : 'red'}>
                Diferencia: {formatCurrency(totals.difference || 0)}
              </Tag>
            </div>
          }
        />
      )}

      {/* Report Table */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Table
          dataSource={filteredAccounts}
          columns={columns}
          rowKey="code"
          loading={loading}
          pagination={false}
          size="small"
          bordered
          scroll={{ x: 1180 }}
          summary={() => {
            if (!totals) return null;
            return (
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0} colSpan={2} align="right">
                  SUMAS IGUALES TOTALES:
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(totals.initial_debit)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(totals.initial_credit)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text style={{ color: '#1677ff', whiteSpace: 'nowrap' }}>{formatCurrency(totals.period_debit)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text style={{ color: '#722ed1', whiteSpace: 'nowrap' }}>{formatCurrency(totals.period_credit)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(totals.final_debit)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(totals.final_credit)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default TrialBalanceView;
